// Client cache utilities for localStorage

// Cache expiration time (24 hours in milliseconds)
const CACHE_EXPIRATION = 24 * 60 * 60 * 1000;

/**
 * Get cached client data from localStorage
 * @param {string} uid - Client UID/token
 * @returns {object|null} Cached client data or null if not found/expired
 */
export const getCachedClientData = (uid) => {
    try {
        const cacheKey = `client-${uid}`;
        const cachedData = localStorage.getItem(cacheKey);
        
        if (!cachedData) {
            return null;
        }

        const parsed = JSON.parse(cachedData);
        const now = Date.now();
        
        // Check if cache is expired
        if (now - parsed.timestamp > CACHE_EXPIRATION) {
            // Remove expired cache
            localStorage.removeItem(cacheKey);
            return null;
        }

        return parsed.clientData;
    } catch (error) {
        console.error('Error reading client cache:', error);
        return null;
    }
};

/**
 * Save client data to localStorage cache
 * @param {string} uid - Client UID/token
 * @param {object} clientData - Client data to cache (logo, colors, whatsapp, etc.)
 */
export const setCachedClientData = (uid, clientData) => {
    try {
        const cacheKey = `client-${uid}`;
        const dataToCache = {
            clientData: {
                logo_url: clientData.logo_url,
                name: clientData.name,
                configuration: clientData.configuration,
                whatsapp: clientData.whatsapp,
                whatsapp_message: clientData.whatsapp_message,
            },
            timestamp: Date.now()
        };
        
        localStorage.setItem(cacheKey, JSON.stringify(dataToCache));
    } catch (error) {
        console.error('Error saving client cache:', error);
    }
};

/**
 * Clear cached client data
 * @param {string} uid - Client UID/token
 */
export const clearCachedClientData = (uid) => {
    try {
        const cacheKey = `client-${uid}`;
        localStorage.removeItem(cacheKey);
    } catch (error) {
        console.error('Error clearing client cache:', error);
    }
};

/**
 * Clear all expired client caches
 */
export const clearExpiredClientCaches = () => {
    try {
        const keys = Object.keys(localStorage);
        const now = Date.now();
        
        keys.forEach(key => {
            if (key.startsWith('client-')) {
                try {
                    const cached = localStorage.getItem(key);
                    if (cached) {
                        const parsed = JSON.parse(cached);
                        if (now - parsed.timestamp > CACHE_EXPIRATION) {
                            localStorage.removeItem(key);
                        }
                    }
                } catch (e) {
                    // If parsing fails, remove the invalid item
                    localStorage.removeItem(key);
                }
            }
        });
    } catch (error) {
        console.error('Error clearing expired caches:', error);
    }
};
